import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { autoUpdate, flip, offset, useFloating } from "@floating-ui/react";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";
import { GroupBase, MenuListProps } from "react-select";
import { sameWidth } from "../../../../../../core/utils/floatingUI";
import { useMergedRef } from "../../../../../../utils";
import { DropdownList } from "../../../../../Dropdown/DropdownList";
import { useGetFloatingUIPortalElement } from "../../../../../FloatingUIPortal";
import { MenuPropsContext } from "../Menu/MenuPropsContext";
import { MenuPortalPropsContext } from "../MenuPortal/MenuPortalPropsContext";

export function MenuList<Option, IsMulti extends boolean, Group extends GroupBase<Option>>
({ children, innerProps, innerRef, ...props }: MenuListProps<Option, IsMulti, Group>) {
  const parentNode = useGetFloatingUIPortalElement();
  const scrollRef = useRef<OverlayScrollbarsComponent>();
  const menuPortalProps = useContext(MenuPortalPropsContext);
  const menuProps = useContext(MenuPropsContext);
  const [ floatingUIElement, setFloatingUIElement ] = useState<HTMLElement | null>(null);

  const referenceElement = menuPortalProps?.controlElement?.parentElement?.parentElement;
  const { refs: { setReference, setFloating }, floatingStyles, update } = useFloating({
    placement: "bottom-start",
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      flip({
        fallbackPlacements: [ "bottom-end", "top-start", "top-end", "bottom-start" ],
      }),
      sameWidth
    ],
  });

  const mergedRef = useMergedRef(menuProps.innerRef, setFloatingUIElement, setFloating);

  useEffect(() => {
    setReference(referenceElement || null);
  }, [ referenceElement, setReference ]);

  useEffect(() => update?.(), [ update, children ]);

  const menuContainsEventTarget = useCallback((e: WheelEvent | MouseEvent) => {
    if (e.target) {
      if (floatingUIElement?.contains(e.target as Node)) {
        //hotfix for inner select components (like inner select)
        if (parentNode?.contains(e.target as Node)) {
          return true;
        }
      }
    }
    return false;
  }, [ floatingUIElement, parentNode ]);

  const customMenuShouldBlockScroll = (props.selectProps as any).customMenuShouldBlockScroll;

  useEffect(() => {
    if (customMenuShouldBlockScroll) {
      const wheelListener = (e: WheelEvent) => {
        if (!menuContainsEventTarget(e)) e.preventDefault();
      };

      const mouseMoveListener = (e: MouseEvent) => {
        if (!menuContainsEventTarget(e)) e.stopImmediatePropagation();
      };

      document.addEventListener("mousemove", mouseMoveListener);
      document.addEventListener("wheel", wheelListener, { passive: false });
      return () => {
        document.removeEventListener("wheel", wheelListener);
        document.removeEventListener("mousemove", mouseMoveListener);
      };
    }
  }, [ customMenuShouldBlockScroll, menuContainsEventTarget ]);

  useEffect(() => {
    scrollRef.current?.osInstance()
      ?.scroll({ top: 0 });
  }, []);

  if (!parentNode || !menuPortalProps?.appendTo || !referenceElement) {
    return <></>;
  }

  return (
    createPortal(
      <DropdownList
        {...menuProps.innerProps}
        ref={mergedRef}
        style={{
          ...floatingStyles,
          maxHeight: `min(${menuProps.maxMenuHeight}px, 40vh)`,
          minWidth: (props.selectProps as any).minMenuWidth,
        }}
        scrollbarProps={{
          ...innerProps,
          ref: (ref) => {
            scrollRef.current = ref;
            (innerRef as any)(ref?.osInstance()
              ?.getElements()?.viewport);
          },
        }}
      >
        {children}
      </DropdownList>,
      parentNode!,
    )
  );
}